import React, { useState, useEffect, useContext, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { toast } from 'sonner';
import { ArrowLeft, Send } from 'lucide-react';
import { 
  sendMessage as sendFirebaseMessage, 
  subscribeToMessages, 
  markMessagesAsRead,
  setTypingIndicator,
  subscribeToTyping
} from '../services/realtimeChat';

const Chat = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const { user, language } = useContext(AuthContext);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [typingUsers, setTypingUsers] = useState([]);
  const messagesEndRef = useRef(null);
  const typingTimeoutRef = useRef(null);

  // Initialize chat room and subscribe to real-time messages
  useEffect(() => {
    if (!jobId || !user) {
      console.log('❌ Chat: Missing jobId or user', { jobId, user: user?.id });
      return;
    }

    console.log('🔵 Chat: Initializing for jobId:', jobId, 'userId:', user.id);
    setLoading(true);
    
    // Initialize chat room via backend (ensures Firebase chat exists)
    const initializeChat = async () => {
      try {
        const API = process.env.REACT_APP_BACKEND_URL + '/api';
        const token = localStorage.getItem('helply_token');
        await fetch(`${API}/chat/${jobId}/initialize`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        console.log('✅ Chat room initialized/verified');
      } catch (error) {
        console.error('⚠️ Failed to initialize chat room:', error);
        // Continue anyway - chat room might already exist
      }
    };
    
    // Initialize chat room first
    initializeChat();
    
    // Subscribe to messages
    const unsubscribeMessages = subscribeToMessages(jobId, (newMessages) => {
      console.log('📨 Chat: Received messages update', newMessages.length);
      setMessages(newMessages);
      setLoading(false);
      
      // Mark messages as read
      markMessagesAsRead(jobId, user.id);
    });

    // Subscribe to typing indicators
    const unsubscribeTyping = subscribeToTyping(jobId, user.id, (users) => {
      setTypingUsers(users);
    });

    // Cleanup subscriptions
    return () => {
      console.log('🔴 Chat: Cleaning up subscriptions');
      unsubscribeMessages();
      unsubscribeTyping();
    };
  }, [jobId, user]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Handle typing indicator
  const handleTyping = () => {
    setTypingIndicator(jobId, user.id, user.name, true);
    
    // Clear previous timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    
    // Set timeout to clear typing indicator
    typingTimeoutRef.current = setTimeout(() => {
      setTypingIndicator(jobId, user.id, user.name, false);
    }, 2000);
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    setSending(true);
    try {
      await sendFirebaseMessage(jobId, user.id, user.name, newMessage);
      setNewMessage('');
      setTypingIndicator(jobId, user.id, user.name, false);
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const content = {
    en: {
      chat: 'Chat',
      typePlaceholder: 'Type a message...',
      send: 'Send'
    },
    de: {
      chat: 'Chat',
      typePlaceholder: 'Nachricht eingeben...',
      send: 'Senden'
    },
    it: {
      chat: 'Chat',
      typePlaceholder: 'Scrivi un messaggio...',
      send: 'Invia'
    },
    fr: {
      chat: 'Chat',
      typePlaceholder: 'Tapez un message...',
      send: 'Envoyer'
    }
  };

  const t = content[language] || content.en;

  // Navigate back to chat list or dashboard
  const handleBack = () => {
    // Go to chat list first, if coming from there
    navigate('/chats');
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col" data-testid="chat-page">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 p-4 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto flex items-center space-x-4">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={handleBack} 
            data-testid="back-btn"
            className="flex items-center gap-2"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="hidden sm:inline">{language === 'en' ? 'Back' : language === 'it' ? 'Indietro' : language === 'fr' ? 'Retour' : 'Zurück'}</span>
          </Button>
          <h1 className="text-xl font-bold font-heading">{t.chat}</h1>
        </div>
      </header>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4">
        <div className="max-w-4xl mx-auto space-y-4">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
            </div>
          ) : messages.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              {language === 'en' ? 'No messages yet. Start the conversation!' : language === 'it' ? 'Nessun messaggio ancora. Inizia la conversazione!' : language === 'fr' ? 'Aucun message encore. Commencez la conversation!' : 'Noch keine Nachrichten. Starten Sie das Gespräch!'}
            </div>
          ) : (
            messages.map((msg) => {
              const isOwn = msg.senderId === user.id;
              return (
                <div
                  key={msg.id}
                  className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}
                  data-testid={`message-${msg.id}`}
                >
                  <div className={`flex items-start space-x-2 max-w-md ${isOwn ? 'flex-row-reverse space-x-reverse' : ''}`}>
                    <Avatar className="w-8 h-8">
                      <AvatarFallback className="text-xs">{msg.senderName?.[0] || 'U'}</AvatarFallback>
                    </Avatar>
                    <div>
                      <div
                        className={`rounded-lg p-3 ${
                          isOwn ? 'bg-blue-600 text-white' : 'bg-white border border-gray-200'
                        }`}
                      >
                        <p className="text-sm">{msg.message}</p>
                      </div>
                      <p className="text-xs text-gray-500 mt-1 px-1">
                        {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })
          )}
          
          {/* Typing Indicator */}
          {typingUsers.length > 0 && (
            <div className="flex justify-start">
              <div className="bg-gray-100 rounded-lg p-3 text-sm text-gray-600">
                <span className="italic">
                  {typingUsers[0].userName} {language === 'en' ? 'is typing' : language === 'it' ? 'sta scrivendo' : language === 'fr' ? 'est en train d\'écrire' : 'schreibt'}...
                </span>
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Input - Fixed for mobile visibility */}
      <div className="bg-white border-t-2 border-gray-300 p-3 sm:p-4 sticky bottom-0 shadow-lg z-50 mb-10">
        <form onSubmit={sendMessage} className="max-w-4xl mx-auto flex items-center gap-2 sm:gap-3" data-testid="chat-form">
          <Input
            value={newMessage}
            onChange={(e) => {
              setNewMessage(e.target.value);
              handleTyping();
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                if (newMessage.trim()) {
                  sendMessage(e);
                }
              }
            }}
            placeholder={t.typePlaceholder}
            disabled={sending}
            className="flex-1 h-12 text-base border-2 border-gray-300 focus:border-blue-500 rounded-full px-4"
            data-testid="message-input"
          />
          <Button 
            type="submit" 
            disabled={sending || !newMessage.trim()} 
            data-testid="send-btn"
            className="h-12 w-12 rounded-full bg-blue-600 hover:bg-blue-700 flex-shrink-0 shadow-md"
          >
            {sending ? (
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
            ) : (
              <Send className="w-5 h-5" />
            )}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default Chat;